import { getSchemeSafeUrl } from '@onefootprint/core';
import { getLogger } from '@/idv2/utils/logger';
import type { CompletePayload, SendResultCallback, WebViewAdapterReturn } from '../types';
import { setClientVersionHeaderFromUrl } from './set-client-version-header';

const { logInfo, logWarn } = getLogger({ location: 'idv-webview' });

const generateWebViewAdapter = (): WebViewAdapterReturn => {
  let sendResultCallback: SendResultCallback | undefined;

  const setSendResultCallback = (callback: SendResultCallback) => {
    sendResultCallback = callback;
  };

  const getRedirectUrl = () => {
    const params = new URLSearchParams(document.location.search);
    return params.get('redirect_url');
  };

  const setLocation = (data: Record<string, unknown> = {}) => {
    const redirectUrl = getSchemeSafeUrl(getRedirectUrl());
    if (!redirectUrl) {
      logWarn('No safe redirect_url provided');
      return;
    }

    const params = new URLSearchParams();
    const dataEntries = Object.entries(data);

    dataEntries.forEach(([key, value]) => {
      params.set(key, value as string);
    });

    const url = `${redirectUrl}?${params.toString()}`;
    logInfo(`Redirecting to ${redirectUrl}`);
    window.location.href = url;
  };

  setClientVersionHeaderFromUrl();

  const load = (): Promise<void> => Promise.resolve();

  const close = (): void => {
    logInfo('Closing footprint from web view adapter');
    setLocation({ canceled: true });
  };

  const cancel = (): void => {
    logInfo('Canceling footprint from web view adapter');
    setLocation({ canceled: true });
  };

  const on: WebViewAdapterReturn['on'] = () => () => undefined;

  const setCompleteTimeout = (location: Record<string, string>, delay = 0) => {
    setTimeout(() => {
      logInfo('Closing footprint after complete timeout from web view adapter');
      setLocation(location);
    }, delay);
  };

  const complete = ({ validationToken, delay = 0, authToken, deviceResponse }: CompletePayload): void => {
    logInfo('Completing footprint from web view adapter');
    const location: Record<string, string> = {
      validation_token: validationToken,
    };
    if (authToken && deviceResponse && sendResultCallback) {
      sendResultCallback(authToken, deviceResponse)
        .then(token => {
          if (token) {
            location.sdk_args_token = token;
          }
        })
        .finally(() => {
          setCompleteTimeout(location, delay);
        });
    } else {
      setCompleteTimeout(location, delay);
    }
  };

  const relayToComponents = (response: { authToken: string; vaultingToken: string }) => {
    logInfo('Completing auth footprint from web view adapter');
    const location: Record<string, string> = {
      auth_token: response.authToken,
      components_vault_token: response.vaultingToken,
    };
    setLocation(location);
  };

  return {
    auth: () => undefined,
    backButtonVisibilityChange: () => {},
    requirementChanged: () => {},
    resized: () => {},
    relayToComponents: relayToComponents,
    setSendResultCallback,
    load,
    cancel,
    close,
    complete,
    on,
  };
};

export default generateWebViewAdapter;
